/**
 * Types for the "Reference Documents" row cell — shared by Audited and Provisional Financials.
 * Lives in `common/` (not either feature module) so it stays mountable with no cross-module
 * import.
 */

/**
 * One source file attached to a period header — mirrors underwriting-engine's
 * `SourceFile` wire type. `docId` is a string (`mis_file_metadata.docId` is a
 * varchar) and must be `Number(...)`-ed before use with `getPresignedUrl`. The backend also
 * sends a `provenance` ('FILE' | 'VENDOR') discriminator that the UI deliberately does not
 * consume — see `label` below.
 */
export interface SourceFile {
    docId: string;
    fileName: string;
    periodStart: string;
    /**
     * The period/snapshot end date (`YYYY-MM-DD`). For a Balance Sheet source file this can
     * differ from `periodStart` by up to a full financial year (a BS bucket often spans FY-start
     * through the snapshot date). The Balance Sheet tooltip/period copy is keyed off this field,
     * not `periodStart`.
     */
    periodEnd: string;
    /** Present only for a 'VENDOR' file — a clean display name (e.g. "MCA AOC-4 — FY24-25") shown
     *  instead of `fileName`, which for a vendor fetch is the company's raw legal-name filename. */
    label?: string;
}

/**
 * Mirrors the backend's `sourceFilesStatus` field. Absent entirely on a header response means
 * "not a tagged surface" (Audited, PDF report, the metrics path) — that case is modelled by the
 * prop simply being `undefined`, not by a third enum member.
 */
export type SourceFilesStatus = 'OK' | 'UNAVAILABLE';

/** Which of the four mutually exclusive cells this instance renders. NONE draws nothing at all —
 *  the response was never tagged, so there is no cell to show. */
export enum SourceFileTagDisplayState {
    NONE = 'none',
    FILES = 'files',
    EMPTY = 'empty',
    FAILURE = 'failure',
}

/**
 * Which statement this tag renders for — not read internally today (kept on the prop for a
 * future per-row need); values match this app's own `ProvisionalStatementKey`, not the backend's
 * wire keys.
 */
export type SourceStatement = 'balanceSheet' | 'profitAndLoss' | 'cashFlow';

export interface SourceFileTagProps {
    sourceFiles?: SourceFile[];
    status?: SourceFilesStatus;
    statement?: SourceStatement;
    /** The column's own period label (e.g. `FY'27 (Nov'26)`), shown in the "+ N more" popover's
     *  header. Not the per-file period suffix — see SourceFileTag.tsx. */
    columnLabel?: string;
    /** Opens the file viewer for one source file. */
    onOpen: (docId: string, fileName: string) => void;
}

/**
 * The slice of a period header the "Reference Documents" row reads. Each surface's own header type
 * (the statement table's, the Financial Summary's) satisfies it structurally.
 */
export interface ReferenceDocumentsHeader {
    name: string;
    sourceFiles?: SourceFile[];
}

export interface ReferenceDocumentsRowConfig<THeader extends ReferenceDocumentsHeader> {
    /** The response-level lookup status. `undefined` means the response was never tagged, so there is no row. */
    sourceFilesStatus?: SourceFilesStatus;
    onOpen: SourceFileTagProps['onOpen'];
    /** Set by the statement tables, which know which statement they show; the Financial Summary has none. */
    statement?: SourceStatement;
    /** Titles the "+ N more" popover. Defaults to `header.name`; a surface that relabels its column header passes
     *  the same relabel here, so the popover never names a different column than the one it sits under. */
    getColumnLabel?: (header: THeader) => string;
}

export interface ReferenceDocumentsRowModel<THeader extends ReferenceDocumentsHeader> {
    /** Text of the row's first (label) cell. */
    label: string;
    /** Every prop one column's `SourceFileTag` takes. */
    getTagProps: (header: THeader) => SourceFileTagProps;
}
